import axios, { type AxiosError, AxiosHeaders, type AxiosResponse } from 'axios'
import dayjs from 'dayjs'
import { serialize } from 'object-to-formdata'
import { useEffect, useRef, useState } from 'react'
import type { ISession, ISessionDetailed, ISsoRefreshResponse } from '@/modules/_misc/Session'
import type { IAppAuthContext } from '@/services/Auth'

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _retry?: boolean
  }
  interface AxiosRequestConfig {
    _retry?: boolean
  }
}

//  ///////////////////////
//  MARK: TYPES
export interface IHAxiosInterceptors {
  onRequest?: number
  onResponse?: number
}

//  ///////////////////////
//  MARK: FUNCTIONS
type TData = {
  [key: string | number]: TData | string | number | boolean | Date | undefined | null
}

const isoDateFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d*)?(?:[+-]\d{2}:?\d{2}|Z)?$/

export function isIsoDateString(value: unknown): boolean {
  return typeof value === 'string' ? isoDateFormat.test(value) : false
}

//  ///////////////////////
//  MARK: REQUEST PARSER
function doesContainFile<T>(body: T): boolean {
  if (body === null || body === undefined || typeof body !== 'object') return false

  for (const key of Object.keys(body)) {
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const value = body[key]
    if (value instanceof File) {
      return true
    }
    if (typeof value === 'object' && doesContainFile(value)) {
      return true
    }
  }

  return false
}

function removeMilliseconds<T>(body: T): T {
  if (body === null || body === undefined || typeof body !== 'object') return body

  for (const key of Object.keys(body)) {
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const value = body[key]

    if (isIsoDateString(value)) {
      // @ts-expect-error
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      body[key] = new Date(value)
    } else if (typeof value === 'object' && !(value instanceof Date) && value !== null) {
      // @ts-expect-error
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      body[key] = removeMilliseconds(value)
    }

    // @ts-expect-error
    if (body[key] instanceof Date) {
      // @ts-expect-error
      body[key] = dayjs(body[key]).set('millisecond', 0).toDate()
    }
  }

  return body
}

//  ///////////////////////
//  MARK: RESPONSE PARSER
export function parseClazz<T>(body: T): T {
  if (body === null || body === undefined || typeof body !== 'object') return body

  for (const key of Object.keys(body)) {
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const value = body[key]

    if (key === 'clazz' && typeof value === 'string') {
      // @ts-expect-error
      // eslint-disable-next-line prefer-destructuring
      body[key] = value.split('$')[0]
    } else if (typeof value === 'object' && !(value instanceof Date) && value !== null) {
      // @ts-expect-error
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      body[key] = parseClazz(value)
    }
  }

  return body
}

export function parseDates<T>(body: T): T {
  if (body === null || body === undefined || typeof body !== 'object') return body

  for (const key of Object.keys(body)) {
    // @ts-expect-error
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const value = body[key]
    if (isIsoDateString(value)) {
      // @ts-expect-error
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      body[key] = new Date(value)
    } else if (typeof value === 'object' && !(value instanceof Date) && value !== null) {
      // @ts-expect-error
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      body[key] = parseDates(value)
    }
  }

  return body
}

//  ///////////////////////
//  MARK: DEFAULT INTERCEPTORS
axios.defaults.timeout = 0

axios.interceptors.request.use((request) => {
  if (typeof request.data === 'object') {
    request.data = removeMilliseconds(request.data as TData)

    if (doesContainFile(request.data)) {
      request.data = serialize(request.data, {
        dotsForObjectNotation: true,
        indices: true,
      })
    }
  }

  return request
})

axios.interceptors.response.use(
  (response) => {
    response.data = parseClazz(response.data as TData)
    response.data = parseDates(response.data as TData)

    return response
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (err: AxiosError<any>) => {
    if (err.response?.data?.total && err.response?.data?.total > 0) {
      try {
        // eslint-disable-next-line no-underscore-dangle
        err.message = err.response.data._embedded.errors[0].message as string
      } catch {
        err.message = "Couldn't find any message."
      }
    }

    return Promise.reject(err)
  },
)

//  ///////////////////////
//  MARK: TOKEN REFRESH
// Refresh dédupliqué : plusieurs 401 concurrents (ou un 401 + un refresh
// proactif) pour le même refresh_token partagent la même requête en vol.
const PROMISES: Record<string, Promise<AxiosResponse<ISession>> | undefined> = {}

// Intervalle du refresh proactif — doit rester < « SSO Session Idle » Keycloak
// (défaut 30 min) pour empêcher la session online de s'éteindre en arrière-plan.
const PROACTIVE_REFRESH_INTERVAL_MS = 10 * 60 * 1000

// Marge sous laquelle, au retour d'un onglet en arrière-plan, on rafraîchit tout
// de suite (les timers sont throttlés quand l'onglet n'est pas visible).
const PROACTIVE_REFRESH_SKEW_MS = 5 * 60 * 1000

// Un JWT Keycloak (session SSO) a exactement 3 segments séparés par des points.
// Les tokens login/password (spring-security-rest) n'en ont pas 3.
function isSsoAccessToken(accessToken: string): boolean {
  return accessToken.split('.').length === 3
}

function hasUsableRefreshToken(session: ISessionDetailed): boolean {
  return isSsoAccessToken(session.access_token)
    ? !!session.keycloak_refresh_token
    : !!session.refresh_token
}

// Échec d'auth définitif = le endpoint de refresh répond 400/401 (refresh_token
// invalide/expiré/révoqué). Absence de réponse (réseau) ou 5xx = transitoire :
// dans ce cas on NE déconnecte PAS, l'utilisateur réessaiera.
function isDefinitiveAuthFailure(err: unknown): boolean {
  const status = (err as AxiosError)?.response?.status
  return status === 400 || status === 401
}

// Rejoue le refresh_token courant et renvoie la réponse. Ne gère pas le logout :
// c'est à l'appelant de décider. Partagé par le chemin réactif (401) et proactif.
function performTokenRefresh(session: ISessionDetailed): Promise<AxiosResponse<ISession>> {
  if (isSsoAccessToken(session.access_token)) {
    const keycloakRefreshToken = session.keycloak_refresh_token
    if (!keycloakRefreshToken) {
      return Promise.reject(new Error('missing keycloak_refresh_token'))
    }

    const cacheKey = `sso:${keycloakRefreshToken}`
    let promise = PROMISES[cacheKey]
    if (!promise) {
      promise = axios
        .post<ISsoRefreshResponse>(
          '/auth/sso/refresh',
          { keycloak_refresh_token: keycloakRefreshToken },
          { _retry: true, headers: { Authorization: '' } },
        )
        .finally(() => {
          PROMISES[cacheKey] = undefined
        })
      PROMISES[cacheKey] = promise
    }
    return promise
  }

  const refreshToken = session.refresh_token
  if (!refreshToken) {
    return Promise.reject(new Error('missing refresh_token'))
  }

  let promise = PROMISES[refreshToken]
  if (!promise) {
    const formData = new FormData()
    formData.append('grant_type', 'refresh_token')
    formData.append('refresh_token', refreshToken)

    promise = axios
      .post<ISession>('/api/access_token', formData, {
        _retry: true,
        headers: { Authorization: '' },
      })
      .finally(() => {
        PROMISES[refreshToken] = undefined
      })
    PROMISES[refreshToken] = promise
  }
  return promise
}

//  ///////////////////////
//  MARK: EXPORTS
export const useAxiosInterceptors = (auth: IAppAuthContext): boolean => {
  // Mis à jour synchroniquement pendant le render. Les intercepteurs et le
  // refresh proactif lisent depuis cette ref : la requête en vol prend le token
  // courant (pas celui du previous render) et le refresh utilise toujours le
  // refresh_token à jour (rotation Keycloak).
  const authRef = useRef(auth)
  authRef.current = auth

  const [mounted, setMounted] = useState(false)

  // Enregistrés une seule fois, même déconnecté : réenregistrés à chaque session,
  // ils manquaient les fetch SWR partis au login (effets layout des enfants,
  // avant cet effet) → 401 sans refresh, et SWR ne réessayait que 5 à 10 s après.
  useEffect(() => {
    const onRequestId = axios.interceptors.request.use((config) => {
      const current = authRef.current.active
      if (current && config.url?.startsWith('/api')) {
        if (!config.headers) {
          config.headers = new AxiosHeaders()
        }

        if (typeof config.headers.Authorization !== 'string') {
          config.headers.Authorization = `${current.token_type} ${current.access_token}`
        } else if (config.headers.Authorization === '') {
          delete config.headers.Authorization
        }

        if (typeof config.headers['X-User'] !== 'string') {
          config.headers['X-User'] = current.username
        }

        const shareholderId = current.globalShareholder?.id
        if (shareholderId != null && typeof config.headers['X-Shareholder-Id'] !== 'string') {
          config.headers['X-Shareholder-Id'] = String(shareholderId)
        }
      }

      return config
    })

    const onResponseId = axios.interceptors.response.use(
      (response) => response,
      async (err: AxiosError<unknown>) => {
        const originalRequest = err.config
        const status = err.response?.status

        // eslint-disable-next-line no-underscore-dangle
        if (status === 401 && originalRequest && !originalRequest._retry) {
          // eslint-disable-next-line no-underscore-dangle
          originalRequest._retry = true

          const current = authRef.current.active
          if (!current) {
            throw err
          }

          // Pas de refresh_token exploitable → logout immédiat (inchangé).
          if (!hasUsableRefreshToken(current)) {
            authRef.current.logout?.()
            throw err
          }

          let response: AxiosResponse<ISession>
          try {
            response = await performTokenRefresh(current)
          } catch (refreshErr) {
            // On ne déconnecte QUE sur un échec d'auth définitif (refresh_token
            // invalide/expiré/révoqué → 400/401). Un 5xx ou un timeout réseau est
            // transitoire : on préserve la session, l'utilisateur réessaiera.
            if (isDefinitiveAuthFailure(refreshErr)) {
              authRef.current.logout?.()
            }
            throw refreshErr
          }

          authRef.current.updateSession(current.username, response)

          return axios({
            ...originalRequest,
            _retry: true,
            headers: {
              ...originalRequest.headers,
              Authorization: `${response.data.token_type} ${response.data.access_token}`,
            },
          })
        }

        throw err
      },
    )

    setMounted(true)

    return () => {
      axios.interceptors.request.eject(onRequestId)
      axios.interceptors.response.eject(onResponseId)
    }
  }, [])

  // Refresh proactif — timer indépendant du cycle des intercepteurs, pour ne pas
  // être réinitialisé par des changements d'état bénins (globalEntity, etc.).
  // Maintient la session Keycloak vivante AVANT l'expiry du JWT local : sans ça,
  // le refresh réactif (sur 401) n'arrive qu'à l'expiry du JWT local (~1 h) alors
  // que la session SSO « online » de Keycloak a déjà dépassé son « SSO Session
  // Idle » (défaut 30 min) → déconnexion malgré une activité constante.
  useEffect(() => {
    const proactiveRefresh = async (): Promise<void> => {
      const current = authRef.current.active
      if (!current || !hasUsableRefreshToken(current)) {
        return
      }
      try {
        const response = await performTokenRefresh(current)
        authRef.current.updateSession(current.username, response)
      } catch {
        // Best-effort : aucun logout sur échec proactif. Si le refresh_token est
        // réellement mort, le prochain 401 déclenchera le logout côté réactif —
        // on ne déconnecte jamais sur un simple hoquet réseau.
      }
    }

    const interval = window.setInterval(() => {
      void proactiveRefresh()
    }, PROACTIVE_REFRESH_INTERVAL_MS)

    // Onglet revenu au premier plan (timers throttlés en arrière-plan) : si le
    // JWT local est proche de l'expiry, on rafraîchit immédiatement.
    const onVisibilityChange = (): void => {
      if (document.visibilityState !== 'visible') {
        return
      }
      const current = authRef.current.active
      if (current?.access_expires_at == null) {
        return
      }
      if (current.access_expires_at - Date.now() < PROACTIVE_REFRESH_SKEW_MS) {
        void proactiveRefresh()
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      window.clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [])

  return mounted
}
